import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import { money, fmt, errMsg } from '../../utils/helpers';
import { Spinner, Modal, ConfirmModal, Field, Select } from '../../components/common/UI';
import { Plus, Trash2, Package } from 'lucide-react';
import toast from 'react-hot-toast';

export function OrderComponentsPanel({ orderId, orderItems = [] }) {
  const [components, setComponents] = useState([]);
  const [warehouseItems, setWarehouseItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addModal, setAddModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [form, setForm] = useState({ order_item_id: '', warehouse_item_id: '', quantity: 1 });
  const [filterCat, setFilterCat] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: comps }, { data: witems }, { data: cats }] = await Promise.all([
        api.get('/warehouse/order/' + orderId + '/components'),
        api.get('/warehouse/items', { params: { active_only: 'true' } }),
        api.get('/warehouse/categories'),
      ]);
      setComponents(comps);
      setWarehouseItems(witems);
      setCategories(cats);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(false); }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const filteredWarehouseItems = filterCat
    ? warehouseItems.filter(i => i.category_id === +filterCat)
    : warehouseItems;

  const warehouseOpts = filteredWarehouseItems.map(i => ({
    value: i.id,
    label: `${i.name}${i.sku ? ' [' + i.sku + ']' : ''} — остаток: ${(+i.stock).toLocaleString('ru-RU', { maximumFractionDigits: 3 })} ${i.unit}`,
  }));

  const catOpts = categories.map(c => ({ value: c.id, label: c.name }));

  const orderItemOpts = [
    { value: '', label: '— Весь заказ (без привязки к позиции) —' },
    ...orderItems.filter(i => i.item_type === 'product').map(i => ({
      value: i.id,
      label: i.product_name || i.kit_name || '—',
    })),
    ...orderItems.filter(i => i.item_type === 'kit').map(i => ({
      value: i.id,
      label: `[Комплект] ${i.kit_name}`,
    })),
  ];

  const selectedWarehouseItem = warehouseItems.find(i => i.id === +form.warehouse_item_id);

  const addComponent = async () => {
    if (!form.warehouse_item_id || !form.quantity || +form.quantity <= 0) {
      toast.error('Укажите товар и количество'); return;
    }
    // Проверяем остаток
    if (selectedWarehouseItem && +form.quantity > +selectedWarehouseItem.stock) {
      toast.error(`Недостаточно остатка. Доступно: ${selectedWarehouseItem.stock} ${selectedWarehouseItem.unit}`);
      return;
    }
    setSaving(true);
    try {
      await api.post('/warehouse/order/' + orderId + '/components', form);
      toast.success('Комплектующее добавлено и списано со склада');
      setAddModal(false);
      setForm({ order_item_id: '', warehouse_item_id: '', quantity: 1 });
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const deleteComponent = async (comp) => {
    try {
      await api.delete('/warehouse/order/' + orderId + '/components/' + comp.id);
      toast.success('Удалено, остаток возвращён на склад');
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  // Группируем по позиции заказа
  const grouped = {};
  components.forEach(c => {
    const key = c.order_item_id || 'order';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(c);
  });

  if (loading) return <div className="flex justify-center py-8"><Spinner /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">
          Складские позиции, используемые при изготовлении. Списываются при добавлении, возвращаются при удалении.
        </p>
        <button className="btn-primary shrink-0" onClick={() => {
          setForm({ order_item_id: '', warehouse_item_id: '', quantity: 1 });
          setAddModal(true);
        }}>
          <Plus size={14} /> Добавить
        </button>
      </div>

      {!components.length ? (
        <div className="text-center py-10 text-gray-600">
          <Package size={28} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">Нет комплектующих</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([key, comps]) => {
            const orderItem = key === 'order' ? null : orderItems.find(i => i.id === +key);
            return (
              <div key={key}>
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                  <Package size={12} />
                  {orderItem
                    ? (orderItem.product_name || orderItem.kit_name || '—')
                    : 'Общие для заказа'}
                </div>
                <div className="space-y-1.5">
                  {comps.map(c => (
                    <div key={c.id} className="flex items-center gap-3 p-2.5 bg-gray-800/60 border border-gray-800 rounded-lg group">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-gray-200">{c.item_name}</div>
                        {c.sku && <div className="text-xs text-gray-600 mono">{c.sku}</div>}
                      </div>
                      <div className="text-sm mono text-gray-300 shrink-0">
                        {(+c.quantity).toLocaleString('ru-RU', { maximumFractionDigits: 3 })} {c.unit}
                      </div>
                      <button className="btn-ghost p-1 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                        onClick={() => setDeleteConfirm(c)}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Итог */}
          <div className="pt-2 border-t border-gray-800 text-xs text-gray-500">
            Всего позиций: {components.length}
          </div>
        </div>
      )}

      {/* Модалка добавления */}
      {addModal && (
        <Modal title="Добавить комплектующее" onClose={() => setAddModal(false)} size="md">
          <div className="space-y-4">
            <Field label="Привязать к позиции заказа">
              <Select value={form.order_item_id} onChange={v => setForm(f => ({ ...f, order_item_id: v }))}
                options={orderItemOpts} />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Категория склада">
                <Select value={filterCat} onChange={v => { setFilterCat(v); setForm(f => ({ ...f, warehouse_item_id: '' })); }}
                  options={catOpts} placeholder="Все категории" />
              </Field>
              <div></div>
            </div>

            <Field label="Товар со склада" required>
              <Select value={form.warehouse_item_id}
                onChange={v => {
                  const item = warehouseItems.find(i => i.id === +v);
                  setForm(f => ({ ...f, warehouse_item_id: v, quantity: item ? 1 : f.quantity }));
                }}
                options={warehouseOpts} placeholder="Выберите товар" />
            </Field>

            {selectedWarehouseItem && (
              <div className="flex items-center gap-2 p-2.5 bg-gray-800/60 rounded-lg border border-gray-800 text-xs">
                <span className="text-gray-500">Остаток на складе:</span>
                <span className={`mono font-medium ${+selectedWarehouseItem.stock <= 0 ? 'text-red-400' : 'text-teal-400'}`}>
                  {(+selectedWarehouseItem.stock).toLocaleString('ru-RU', { maximumFractionDigits: 3 })} {selectedWarehouseItem.unit}
                </span>
              </div>
            )}

            <Field label={`Количество${selectedWarehouseItem ? ' (' + selectedWarehouseItem.unit + ')' : ''}`} required>
              <input type="number" min="0.001" step="1" className="input" value={form.quantity}
                max={selectedWarehouseItem ? +selectedWarehouseItem.stock : undefined}
                onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} />
            </Field>

            <p className="text-xs text-yellow-600">
              ⚠ При добавлении указанное количество будет сразу списано со склада по FIFO
            </p>

            <div className="flex justify-end gap-2 pt-2">
              <button className="btn-secondary" onClick={() => setAddModal(false)}>Отмена</button>
              <button className="btn-primary" onClick={addComponent} disabled={saving}>
                {saving ? <Spinner size={14} /> : <><Package size={14} /> Добавить и списать</>}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {deleteConfirm && (
        <ConfirmModal
          message={`Удалить "${deleteConfirm.item_name}" (${(+deleteConfirm.quantity).toLocaleString('ru-RU', { maximumFractionDigits: 3 })} ${deleteConfirm.unit})? Остаток будет возвращён на склад.`}
          onConfirm={() => { deleteComponent(deleteConfirm); setDeleteConfirm(null); }}
          onClose={() => setDeleteConfirm(null)}
        />
      )}
    </div>
  );
}
